import { useEffect, useState, lazy, Suspense } from "react";
import { Switch, Route, useLocation } from "wouter";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { I18nProvider } from "@/lib/i18n";
import { Header } from "@/components/layout/header";
import Home from "@/pages/home";

const Footer = lazy(() => import("@/components/layout/footer").then(m => ({ default: m.Footer })));
const Toaster = lazy(() => import("@/components/ui/toaster").then(m => ({ default: m.Toaster })));
const WhatsAppButton = lazy(() => import("@/components/layout/whatsapp-button").then(m => ({ default: m.WhatsAppButton })));
const ChatbotButton = lazy(() => import("@/components/layout/chatbot-button").then(m => ({ default: m.ChatbotButton })));
const OrganizationSchema = lazy(() => import("@/components/seo/structured-data").then(m => ({ default: m.OrganizationSchema })));
const Breadcrumbs = lazy(() => import("@/components/seo/breadcrumbs").then(m => ({ default: m.Breadcrumbs })));

const About = lazy(() => import("@/pages/about"));
const Services = lazy(() => import("@/pages/services"));
const Gallery = lazy(() => import("@/pages/gallery"));
const Contact = lazy(() => import("@/pages/contact"));
const FAQ = lazy(() => import("@/pages/faq"));
const Blog = lazy(() => import("@/pages/blog"));
const BlogPost = lazy(() => import("@/pages/blog-post"));
const Videos = lazy(() => import("@/pages/videos"));
const Privacy = lazy(() => import("@/pages/privacy"));
const Terms = lazy(() => import("@/pages/terms"));
const NotFound = lazy(() => import("@/pages/not-found"));

function ScrollToTop() {
  const [location] = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);
  
  return null;
}

function Router() {
  return (
    <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" /></div>}>
      <Switch>
        <Route path="/" component={Home} />
        <Route path="/about" component={About} />
        <Route path="/services" component={Services} />
        <Route path="/gallery" component={Gallery} />
        <Route path="/contact" component={Contact} />
        <Route path="/faq" component={FAQ} />
        <Route path="/blog" component={Blog} />
        <Route path="/blog/:slug" component={BlogPost} />
        <Route path="/videos" component={Videos} />
        <Route path="/privacy" component={Privacy} />
        <Route path="/terms" component={Terms} />
        <Route component={NotFound} />
      </Switch>
    </Suspense>
  );
}

function DeferredWidgets() {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const rIC = typeof window !== "undefined" && "requestIdleCallback" in window ? window.requestIdleCallback : null;
    if (rIC) {
      const id = rIC(() => setReady(true));
      return () => window.cancelIdleCallback(id);
    }
    const id = setTimeout(() => setReady(true), 200);
    return () => clearTimeout(id);
  }, []);
  if (!ready) return null;
  return (
    <>
      <ChatbotButton />
      <WhatsAppButton />
    </>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <I18nProvider>
          <div className="min-h-screen flex flex-col">
            <ScrollToTop />
            <Suspense fallback={null}>
              <OrganizationSchema />
            </Suspense>
            <Header />
            <Suspense fallback={null}>
              <Breadcrumbs />
            </Suspense>
            <main className="flex-1">
              <Router />
            </main>
            <Suspense fallback={null}>
              <Footer />
            </Suspense>
          </div>
          <Suspense fallback={null}>
            <Toaster />
            <DeferredWidgets />
          </Suspense>
      </I18nProvider>
    </QueryClientProvider>
  );
}

export default App;
